import React, { useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { useInventoryContext } from '../contexts/InventoryContext';
import { Car } from '../types/car';
import { CarSummary } from './CarSummary';
import { FormField, inputClass } from './FormField';
import { SubmitError } from './SubmitError';
import { SuccessState } from './SuccessState';
import { VehicleSelect } from './VehicleSelect';
import { WhatsAppIcon } from './WhatsAppIcon';
import { submitLead } from '../utils/leads';
import { whatsappMessageUrl } from '../utils/whatsapp';
interface InquiryFormProps {
  car?: Car | null;
  onClose: () => void;
}
type ContactChannel = 'WhatsApp' | 'Llamada' | 'Email';
type Fields = {
  name: string;
  phone: string;
  email: string;
  message: string;
};
const channels: ContactChannel[] = ['WhatsApp', 'Llamada', 'Email'];
export function InquiryForm({
  car,
  onClose
}: InquiryFormProps) {
  const {
    cars
  } = useInventoryContext();
  const [vehicleId, setVehicleId] = useState('');
  const [fields, setFields] = useState<Fields>({
    name: '',
    phone: '',
    email: '',
    message: car ? `Hola, me interesa el ${car.brand} ${car.model} ${car.version} ${car.year}. ¿Sigue disponible?` : 'Hola, quiero recibir asesoramiento para comprar un vehículo.'
  });
  const [channel, setChannel] = useState<ContactChannel>('WhatsApp');
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const update = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFields(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const handleVehicleChange = (id: string) => {
    setVehicleId(id);
    const picked = cars.find(c => c.id === id);
    setFields(prev => ({
      ...prev,
      message: picked ? `Hola, me interesa el ${picked.brand} ${picked.model} ${picked.version} ${picked.year}. ¿Sigue disponible?` : 'Hola, quiero recibir asesoramiento para comprar un vehículo.'
    }));
  };
  if (status === 'success') {
    const target = car ?? cars.find(c => c.id === vehicleId);
    const waText = [`Hola VCARS, soy ${fields.name.trim()}. Les dejé una consulta desde el catálogo:`, '', target ? `🚗 Vehículo: ${target.brand} ${target.model} ${target.version} ${target.year} (${target.id})` : '🚗 Vehículo: todavía no lo decidí', `💬 ${fields.message.trim()}`, '', `📞 ${fields.phone.trim()}${fields.email ? ` · ✉️ ${fields.email.trim()}` : ''}`, `Prefiero contacto por: ${channel}`].join('\n');
    return <SuccessState title="¡Consulta enviada!" message={`Gracias ${fields.name.split(' ')[0]}. Ya recibimos tu consulta. Si querés una respuesta más rápida, envianosla también por WhatsApp.`} onClose={onClose} action={<a href={whatsappMessageUrl(waText)} target="_blank" rel="noopener noreferrer" className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-neon text-sm font-semibold text-ink shadow-neon-btn transition-colors duration-150 hover:bg-neon-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-neon focus-visible:ring-offset-2 focus-visible:ring-offset-panel" data-id="element-322" data-mp="2odpog4gkqwt4">
            <WhatsAppIcon className="h-5 w-5" data-id="element-323" data-mp="373z34dtjsh1p" />
            Enviar también por WhatsApp
          </a>} data-id="element-321" data-mp="24al5rjjol767" />;
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof Fields, string>> = {};
    if (!fields.name.trim()) next.name = 'Ingresá tu nombre';
    if (fields.phone.replace(/\D/g, '').length < 8) next.phone = 'Ingresá un teléfono válido';
    if (channel === 'Email' && !fields.email) next.email = 'Ingresá un email para contactarte';
    if (fields.email && !/^\S+@\S+\.\S+$/.test(fields.email)) next.email = 'Ingresá un email válido';
    if (!fields.message.trim()) next.message = 'Escribí tu consulta';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('loading');
    const target = car ?? cars.find(c => c.id === vehicleId);
    try {
      await submitLead({
        name: fields.name,
        phone: fields.phone,
        email: fields.email,
        reason: 'buy',
        vehicle: target ? `${target.brand} ${target.model} ${target.version} ${target.year}` : 'Sin definir',
        message: `${fields.message.trim()}\n\nPrefiere contacto por: ${channel}`
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  return <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-324" data-mp="3jkoyo24muykq">
      {car ? <CarSummary car={car} data-id="element-325" data-mp="b5y4rj0ld3ds" /> : <FormField label="Vehículo de interés" htmlFor="iq-vehicle" optional data-id="element-326" data-mp="b43k85t8fda8">
          <VehicleSelect id="iq-vehicle" value={vehicleId} onChange={handleVehicleChange} placeholder="Todavía no lo decidí" data-id="element-327" data-mp="fyhlgouksemt" />
        </FormField>}

      <div className="grid gap-4 sm:grid-cols-2" data-id="element-328" data-mp="1voiep1eyh4m8">
        <FormField label="Nombre y apellido" htmlFor="iq-name" error={errors.name} className="sm:col-span-2" data-id="element-329" data-mp="1vnpgiou2bnfp">
          <input id="iq-name" className={inputClass} value={fields.name} onChange={update('name')} autoComplete="name" data-id="element-330" data-mp="2af7uyi79x9wy" />
        </FormField>
        <FormField label="Teléfono" htmlFor="iq-phone" error={errors.phone} data-id="element-331" data-mp="1hnxx28ks1eub">
          <input id="iq-phone" type="tel" className={inputClass} value={fields.phone} onChange={update('phone')} autoComplete="tel" data-id="element-332" data-mp="1cpeaq729u11f" />
        </FormField>
        <FormField label="Email" htmlFor="iq-email" error={errors.email} optional={channel !== 'Email'} data-id="element-333" data-mp="1rnf0skggxinn">
          <input id="iq-email" type="email" className={inputClass} value={fields.email} onChange={update('email')} autoComplete="email" data-id="element-334" data-mp="1mlzgwvq11i6u" />
        </FormField>
      </div>

      <fieldset data-id="element-335" data-mp="2y3iqr6y44jed">
        <legend className="mb-1.5 text-sm font-medium text-white" data-id="element-336" data-mp="2y1xuncw5gsee">¿Cómo preferís que te contactemos?</legend>
        <div className="grid grid-cols-3 gap-2" data-id="element-337" data-mp="355e5rwzqntw5">
          {channels.map(c => {
          const active = c === channel;
          return <button key={c} type="button" aria-pressed={active} onClick={() => setChannel(c)} className={`h-10 rounded-lg border text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon ${active ? 'border-neon bg-neon/10 text-neon' : 'border-line text-white hover:border-neon/60 hover:text-neon'}`} data-id="element-338" data-mp="3f4d52oym6d6h">
                {c}
              </button>;
        })}
        </div>
      </fieldset>

      <FormField label="Mensaje" htmlFor="iq-message" error={errors.message} data-id="element-339" data-mp="23oy99oi55rcy">
        <textarea id="iq-message" rows={4} className={`${inputClass} h-auto resize-none py-3 leading-6`} value={fields.message} onChange={update('message')} data-id="element-340" data-mp="23mu7pd7pt4fu" />
      </FormField>

      {status === 'error' && <SubmitError data-id="element-341" data-mp="lsd0meuix28h" />}

      <button type="submit" disabled={status === 'loading'} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-neon text-sm font-semibold text-ink transition-colors duration-150 hover:bg-neon-bright disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon focus-visible:ring-offset-2 focus-visible:ring-offset-panel" data-id="element-342" data-mp="lrtfyqdhnzqr">
        {status === 'loading' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-343" data-mp="21hqdb8gqyci" />}
        {status === 'loading' ? 'Enviando…' : status === 'error' ? 'Reintentar' : 'Dejar mi consulta'}
      </button>
    </form>;
}